import { type Theme, alpha } from "@mui/material/styles";

import { type GameTone, colors } from "@/theme";

/**
 * A color a game hands to one of the shared pieces.
 *
 * Either an outcome it wants named - `"win"`, `"lose"`, `"push"` - or a color
 * of its own, like baccarat's banker red or a roulette pocket's paint. Naming
 * the outcome rather than passing its color is what lets the banner follow the
 * color scheme: the caller does not know, and does not need to know, that a
 * win is bright mint on a dark page and deep emerald on a light one.
 */
export type ToneValue = GameTone | (string & {});

const TONES: Record<GameTone, true> = { win: true, lose: true, push: true };

const isTone = (value: ToneValue | undefined): value is GameTone =>
  typeof value === "string" && value in TONES;

/**
 * `ToneValue` is `GameTone | string`, so the compiler cannot tell a tone from a
 * typo - and the one that bites is an sx palette path like `"game.win"`, which
 * reads fine but reaches `alpha()` as nonsense and throws at render. Catch it
 * where it is cheap to find rather than in a blank board.
 */
const assertTone = (tone: ToneValue | undefined) => {
  if (
    process.env.NODE_ENV !== "production" &&
    typeof tone === "string" &&
    tone.startsWith("game.")
  ) {
    throw new Error(
      `Pass the tone itself, not its palette path: tone="${tone.slice("game.".length)}", not "${tone}". ` +
        "Palette paths belong in `sx`; the shared pieces resolve a tone themselves.",
    );
  }
};

/**
 * A tone as something `sx` can color with.
 *
 * The mirror of `toneColor`, for the places that hand the same value to a
 * shared piece and to an `sx` prop - video poker names the paying hand in both.
 */
export const toneSx = (tone: ToneValue | undefined) => {
  assertTone(tone);
  return isTone(tone) ? `game.${tone}` : tone;
};

/** The scheme's color for a tone, or a game's own color passed through. */
export const toneColor = (theme: Theme, tone: ToneValue | undefined, fallback?: string) => {
  assertTone(tone);
  if (isTone(tone)) return theme.vars.palette.game[tone];
  return tone ?? fallback ?? theme.vars.palette.game.ink;
};

/**
 * The same, blended.
 *
 * A named tone resolves through its channel token, because `alpha()` cannot
 * blend a CSS variable - `alpha("var(--x)", 0.4)` returns nonsense. A game's
 * own color is a literal, so it blends the ordinary way.
 */
export const toneBlend = (theme: Theme, tone: ToneValue | undefined, opacity: number) => {
  assertTone(tone);
  if (isTone(tone)) {
    return `rgba(${theme.vars.palette.game[`${tone}Channel`]} / ${opacity})`;
  }
  if (tone) return alpha(tone, opacity);
  return `rgba(${theme.vars.palette.game.inkChannel} / ${opacity})`;
};

export interface StageGlowOptions {
  /** The color the stage is lit with. The brand blue unless the game has a reason. */
  tint?: ToneValue;
  /** How much of it reaches the felt. */
  strength?: number;
  /** Where the light comes from, as a background-position. */
  from?: string;
  /** Lay a shaded floor under the light. Felted tables want it; flat stages do not. */
  floor?: boolean;
}

/**
 * The light over a game's stage.
 *
 * Every board had its own hand-written radial gradient, and they landed on
 * eight different opacities between 0.06 and 0.16 for the same effect - close
 * enough that the differences read as a mistake rather than a choice. This
 * takes the one decision a game actually makes (what color its light is, and
 * whether the table has a floor) and writes the rest the same way each time.
 *
 * It needs the theme because both halves are scheme-dependent: the tint may be
 * an outcome color, and the floor is a 45% black on a dark page but a tenth of
 * that on a light one, where the same shade would read as a stain.
 */
export const stageGlow = (
  theme: Theme,
  { tint = colors.prime, strength = 0.12, from = "50% 40%", floor = false }: StageGlowOptions = {},
) =>
  [
    `radial-gradient(ellipse at ${from}, ${toneBlend(theme, tint, strength)}, transparent 62%)`,
    floor &&
      `radial-gradient(ellipse at 50% 100%, ${theme.vars.palette.game.shade}, transparent 60%)`,
  ]
    .filter(Boolean)
    .join(", ");

/**
 * A surface that differs between schemes, for the games whose table is their
 * own color - baccarat's green felt, video poker's navy cabinet.
 *
 * One value means the game wants that color in both schemes.
 */
export type FeltValue = string | { light: string; dark: string };

/**
 * Ink on a game's own table.
 *
 * A felt is an object in the room, not a page surface: baccarat's table is
 * green in daylight too, and its markings are painted white on it either way.
 * So the games that bring their own felt keep white ink in both schemes, and
 * only the games played directly on the page - dice, mines, keno - take the
 * scheme's text color. Getting this wrong is what would put near-black
 * lettering on a dark green table the moment the page went light.
 */
export const FELT_INK = "#ffffff";

/**
 * The card table, in the mascot's colors.
 *
 * The tables were casino green, which was the one large surface on the site
 * that belonged to no part of the brand. The mascot is drawn against a night
 * sky, so that is what its tables are covered in: the same navy the page is
 * built from, a step lighter so a table still reads as an object on it.
 *
 * In the light scheme it is the mascot's blue an hour before night rather than
 * a second navy: the two used to be near enough that a table looked the same
 * in both schemes. It is as bright as a table can go and still carry the white
 * markings and brand-blue accents every table keeps in both.
 */
export const SKY_FELT_LIGHT = "#18499C";

/** The same felt where a game paints it straight onto a canvas or a cell. */
export const SKY_FELT_DEEP = "#0C1C2A";

export const SKY_FELT: { light: string; dark: string } = {
  light: SKY_FELT_LIGHT,
  dark: SKY_FELT_DEEP,
};

/** The crown: the one warm color the artwork keeps, so it marks the top of anything. */
export const CROWN = colors.tooGold;

/**
 * Chip colors, lowest denomination first.
 *
 * Cloud white up through the sky to the night the mascot's own chip is cut
 * from, then the red of the ace it is holding.
 */
const CHIP_RAMP = ["#E6EEF9", "#5BC8FF", colors.prime, "#0B63D6", "#16233A", "#E0475B"] as const;

/**
 * The color of one chip in a tray of `count`.
 *
 * The biggest chip in a tray always wears the crown, however many chips the
 * table deals in - it is the one a player should be able to find without
 * reading the figure on it.
 */
export const chipTone = (index: number, count: number): string =>
  index === count - 1 ? CROWN : CHIP_RAMP[index % CHIP_RAMP.length];

/**
 * The table under a board, and the ink that belongs on it.
 *
 * A game with no felt of its own is played on the page, so it takes both from
 * the scheme; a game with one keeps its table and its white markings.
 */
export const feltStyles = (theme: Theme, felt: FeltValue | undefined) => {
  if (!felt) {
    return {
      backgroundColor: theme.vars.palette.game.felt,
      color: theme.vars.palette.game.ink,
    };
  }
  if (typeof felt === "string") return { backgroundColor: felt, color: FELT_INK };
  return {
    backgroundColor: felt.light,
    color: FELT_INK,
    ...theme.applyStyles("dark", { backgroundColor: felt.dark }),
  };
};
